import { type Module } from 'vuex'
import type { ICitaOdontologicas } from '@/modules/Asistencial/interfaces'

/**
 * Estado compartido del odontograma / control de placa.
 *
 * Antes vivia en refs a nivel de modulo dentro de useOdontograma (un store
 * casero): sin dueño, sin punto de reset y borrable por cualquier onUnmounted.
 * Aca queda centralizado, con reset explicito y acotado por atencion (cittId).
 *
 * - `diligenciados`: por origen (historia | view | impresion) y nombre de
 *   odontograma, si ya fue diligenciado. Lo usa la validacion al cambiar de tab.
 * - `ausentes`: dientes marcados como ausentes en el ODONTOGRAMA. Es el puente
 *   hacia el CONTROL DE PLACA, que los pinta ausentes tambien.
 * - `dientesPresentes`: cantidad calculada por el odontograma, por atencion.
 *   El control de placa la necesita para el indice de placa (antes se leia del
 *   DOM con getElementById('SU_34B_7'), que reventaba si no estaba montado).
 */
export interface StateOdontograma {
  diligenciados: Record<string, Record<string, boolean>>
  ausentes: ICitaOdontologicas[]
  dientesPresentes: Record<number, number>
}

const odontograma: Module<StateOdontograma, unknown> = {
  namespaced: true,
  state: (): StateOdontograma => ({
    diligenciados: {},
    ausentes: [],
    dientesPresentes: {}
  }),
  getters: {
    // Devuelve {} cuando el origen no fue inicializado (evita Object.keys(undefined))
    diligenciadosPorOrigen: (state: StateOdontograma) => (origen: string): Record<string, boolean> =>
      state.diligenciados[origen] ?? {},
    // Los ausentes SIEMPRE se consultan acotados a la atencion en curso
    ausentesPorAtencion: (state: StateOdontograma) => (cittId: number): ICitaOdontologicas[] =>
      state.ausentes.filter(e => e.cittId == cittId),
    dientesPresentesDe: (state: StateOdontograma) => (cittId: number): number | null =>
      state.dientesPresentes[cittId] ?? null
  },
  mutations: {
    setDiligenciado (
      state: StateOdontograma,
      { origen, nombre, diligenciado }: { origen: string, nombre: string, diligenciado: boolean }
    ) {
      if (!state.diligenciados[origen]) state.diligenciados[origen] = {}
      state.diligenciados[origen][nombre] = diligenciado
    },
    limpiarOrigen (state: StateOdontograma, origen: string) {
      delete state.diligenciados[origen]
    },
    setAusentesPorAtencion (
      state: StateOdontograma,
      { cittId, ausentes }: { cittId: number, ausentes: ICitaOdontologicas[] }
    ) {
      state.ausentes = state.ausentes.filter(e => e.cittId != cittId).concat(
        ausentes.filter((e, index) => ausentes.findIndex(d => d.ciodDiente === e.ciodDiente) === index)
      )
    },
    agregarAusente (state: StateOdontograma, diente: ICitaOdontologicas) {
      const yaEsta = state.ausentes.some(
        e => e.ciodDiente == diente.ciodDiente && e.cittId == diente.cittId
      )
      if (!yaEsta) state.ausentes.push(diente)
    },
    quitarAusente (
      state: StateOdontograma,
      { cittId, ciodDiente }: Pick<ICitaOdontologicas, 'cittId' | 'ciodDiente'>
    ) {
      state.ausentes = state.ausentes.filter(
        e => e.cittId != cittId || e.ciodDiente != ciodDiente
      )
    },
    setDientesPresentes (
      state: StateOdontograma,
      { cittId, cantidad }: { cittId: number, cantidad: number }
    ) {
      state.dientesPresentes[cittId] = cantidad
    },
    /** Limpia lo de una atencion puntual (al cerrar/cambiar de atencion) */
    limpiarAtencion (state: StateOdontograma, cittId: number) {
      state.ausentes = state.ausentes.filter(e => e.cittId != cittId)
      delete state.dientesPresentes[cittId]
    },
    resetOdontograma (state: StateOdontograma) {
      state.diligenciados = {}
      state.ausentes = []
      state.dientesPresentes = {}
    }
  }
}

export default odontograma
